import React, { useState, useEffect } from "react";
import {
  PageHeading, useRouteState,
  SearchInput, SearchFiltersContainer,
  // eslint-disable-next-line no-unused-vars
  FilterValue, FilterTypes,
} from "isuppli-react-components";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import Page from "../../Containers/Page/Page";
import DetailedAuditTable from "./DetailedAuditTable/DetailedAuditTable";
import { showError } from "../../store/actions";

import {
  DetailedAuditTrailsMessage, // eslint-disable-line no-unused-vars
  getDetailedAudits,
  DetailedAuditSearchMessage, // eslint-disable-line no-unused-vars
} from "../../http/detailedaudit/detailedaudit";
import { UserDetails } from "../Audit/Audit"; // eslint-disable-line no-unused-vars
import { getUsers, getBusinessUnits } from "../../http/audit/audit";
import { ISuppliServiceTypeOptions } from "../../util/Enumerators/ISuppliServiceType";

const DetailedAudit = () => {
  const [routeSearchTerm, setRouteSearchTerm] = useRouteState("q", "");
  const [searchTerm, setSearchTerm] = useState("");

  const [limit, setLimit] = useRouteState("l", 20);
  const [offset, setOffset] = useRouteState("o", 0);

  const [routeFilterValues, setRouteFilterValues] = useRouteState<
    FilterValue[]
  >("f", []);
  const [filterValues, setFilterValues] = useState<FilterValue[]>(
    routeFilterValues
  );

  const [requestedUserFilterOptions, setRequestedUserFilterOptions] = useState([
    {
      value: 0,
      display: "",
    },
  ]);

  const [businessUnits, setBusinessUnits] = useState([
    {
      value: 0,
      display: "",
    },
  ]);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DetailedAuditTrailsMessage>();

  const location = useLocation();
  const dispatch = useDispatch();

  const baseUrl = "/api/v1/detailedAudits/export";
  const [exportUrl, setExportUrl] = useState(baseUrl);

  const updateExportUrl = (
    searchCriteria: DetailedAuditSearchMessage
  ) => {
    let newExportUrl = `${baseUrl}?limit=${searchCriteria.limit}&offset=${searchCriteria.offset}`;
    if (searchCriteria.startDate) newExportUrl = `${newExportUrl}&startDate=${searchCriteria.startDate}`;
    if (searchCriteria.endDate) newExportUrl = `${newExportUrl}&endDate=${searchCriteria.endDate}`;
    if (searchCriteria.searchTerm) newExportUrl = `${newExportUrl}&searchTerm=${searchCriteria.searchTerm}`;
    if (searchCriteria.requestedUserIdList) newExportUrl = `${newExportUrl}&requestedUserId=${searchCriteria.requestedUserIdList}`;
    if (searchCriteria.businessUnitIdList) newExportUrl = `${newExportUrl}&businessUnitId=${searchCriteria.businessUnitIdList}`;
    if (searchCriteria.serviceTypes) {
      for (let i = 0; i < searchCriteria.serviceTypes.length; i += 1) {
        newExportUrl += `&serviceTypes=${searchCriteria.serviceTypes[i]}`;
      }
    }
    setExportUrl(newExportUrl);
  };

  useEffect(() => {
    const loader = async () => {
      setLoading(true);
      try {
        const serviceTypeFilters: any = routeFilterValues[0];
        const dateFilter: any = routeFilterValues[1];
        const requestedUserFilterValue: any = routeFilterValues[2];
        const requestedBusinessUnitFilterValue: any = routeFilterValues[3];

        let startDate = null;
        let endDate = null;
        if (dateFilter != null) {
          [startDate, endDate] = dateFilter;
        }

        const searchCriteria: DetailedAuditSearchMessage = {
          limit,
          offset,
          startDate: startDate ?? undefined,
          endDate: endDate ?? undefined,
          searchTerm: routeSearchTerm,
          serviceTypes: serviceTypeFilters,
          requestedUserIdList: requestedUserFilterValue ?? [],
          businessUnitIdList: requestedBusinessUnitFilterValue ?? [],
        };

        // api call
        const result = await getDetailedAudits(searchCriteria);
        setData(result);

        // update export url
        updateExportUrl(searchCriteria);
      } catch (error) {
        dispatch(showError());
      }

      setLoading(false);
    };
    loader();
  }, [
    location,
    dispatch,
    limit,
    offset,
    routeSearchTerm,
    routeFilterValues,
  ]);

  useEffect(() => {
    const loader = async () => {
      try {
        const businessUnitFilters: { value: number; display: string }[] = [];
        const staticbusinessUnitData: any = await getBusinessUnits();

        staticbusinessUnitData.forEach((businessUnitData: any) => {
          businessUnitFilters.push({
            value: businessUnitData.id,
            display: businessUnitData.shortName,
          });
        });

        setBusinessUnits(businessUnitFilters);
      } catch (error) {
        dispatch(showError());
      }
    };
    loader();
  }, [dispatch, setBusinessUnits]);

  useEffect(() => {
    const loader = async () => {
      try {
        const result: UserDetails[] = await getUsers();
        setRequestedUserFilterOptions(result);
      } catch (error) {
        dispatch(showError());
      }
    };
    loader();
  }, [dispatch, setRequestedUserFilterOptions]);

  const onPerformSearch = async () => {
    setOffset(0);
    setRouteSearchTerm(searchTerm);
    setRouteFilterValues(filterValues ?? []);
  };

  const clearSearch = () => {
    setRouteSearchTerm("");
    setSearchTerm("");
    setRouteFilterValues([]);
    setFilterValues([]);
  };

  const filters = [
    {
      filterName: "Service Type",
      options: ISuppliServiceTypeOptions(),
      filterType: FilterTypes.MultiSelectDropDown,
    },
    {
      filterName: "Date Range",
      options: [],
      filterType: FilterTypes.DateRangeInput,
    },
    {
      filterName: "Requested Name",
      options: requestedUserFilterOptions,
      filterType: FilterTypes.MultiSelectDropDown,
      canSearch: true,
    },
    {
      filterName: "Business Units",
      propName: "businessUnits",
      options: businessUnits,
      filterType: FilterTypes.MultiSelectDropDown,
      disabled: businessUnits.length <= 1,
    },
  ];

  return (
    <Page>
      <PageHeading heading="Detailed Audit Trail" menuItems={[]} />
      <SearchInput
        searchTerm={searchTerm}
        onSearch={() => {
          setOffset(0);
          setRouteSearchTerm(searchTerm);
        }}
        onChange={(value: string) => {
          setSearchTerm(value);
        }}
        searchPlaceholder="Search for a detailed audit entry"
        className="mb-4"
      />
      <SearchFiltersContainer
        filters={filters}
        buttons={[
          {
            key: "clear",
            label: "Clear",
            onClick: clearSearch,
            color: "secondary",
          },
          {
            key: "search",
            label: "Search",
            onClick: onPerformSearch,
            color: "primary",
          },
        ]}
        className="mb-4"
        filterValues={filterValues ?? []}
        onChange={setFilterValues}
      />
      <div className="d-flex flex-row-reverse mt-1">
        <div>
          <a
            href={`/api/v1/token/getfile?url=${encodeURIComponent(exportUrl)}`}
            download="detailedAudits.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={`btn btn-link w-100 text-right px-0 text-capitalize ${loading ? "disabled" : ""}`}
            onClick={() => {
            }}
          >
            Download PDF
          </a>
        </div>
      </div>
      <DetailedAuditTable
        loading={loading}
        onPageChange={(l, o) => {
          setLimit(l);
          setOffset(o);
        }}
        data={{
          items: data?.items ?? [],
          limit,
          offset,
          totalItems: data?.totalItems ?? 0,
        }}
      />
    </Page>
  );
};

export default DetailedAudit;
